import { baseApi } from "./baseApi";
import type {
  Deal,
  CreateDealData,
  UpdateDealData,
  DealFilters,
  DealOrder,
  DealSelections
} from "../types/deals";

export interface PaginatedDealsResponse {
  success: boolean;
  data: Deal[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

export interface DealResponse {
  success: boolean;
  data: Deal;
  message?: string;
}

export interface DealOrderResponse {
  success: boolean;
  data: DealOrder;
  message?: string;
}

export interface CreateDealOrderData {
  orderId: string;
  quantity?: number;
  selections?: DealSelections;
}

/**
 * Deals API slice
 * Handles all deal-related API calls
 */
export const dealsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all deals with pagination and filters
    getDeals: builder.query<PaginatedDealsResponse, DealFilters | void>({
      query: (params) => {
        if (!params) return "/deals";
        const searchParams = new URLSearchParams();
        if (params.search) searchParams.append("search", params.search);
        if (params.isActive !== undefined) searchParams.append("isActive", String(params.isActive));
        if (params.featured !== undefined) searchParams.append("featured", String(params.featured));
        if (params.dealType) searchParams.append("dealType", params.dealType);
        if (params.page !== undefined) searchParams.append("page", String(params.page));
        if (params.limit !== undefined) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return `/deals${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Deal" as const, id })),
              { type: "Deal", id: "LIST" },
            ]
          : [{ type: "Deal", id: "LIST" }],
    }),

    // Get a single deal by ID
    getDeal: builder.query<DealResponse, string>({
      query: (id) => `/deals/${id}`,
      providesTags: (_, __, id) => [{ type: "Deal", id }],
    }),

    // Create a new deal (admin only)
    createDeal: builder.mutation<DealResponse, CreateDealData>({
      query: (dealData) => ({
        url: "/deals",
        method: "POST",
        body: dealData,
      }),
      invalidatesTags: [{ type: "Deal", id: "LIST" }],
    }),

    // Update a deal (admin only)
    updateDeal: builder.mutation<DealResponse, { id: string; data: UpdateDealData }>({
      query: ({ id, data }) => ({
        url: `/deals/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Deal", id },
        { type: "Deal", id: "LIST" },
      ],
    }),

    // Delete a deal (admin only)
    deleteDeal: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/deals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "Deal", id },
        { type: "Deal", id: "LIST" },
      ],
    }),

    // Add deal to order/cart
    createDealOrder: builder.mutation<DealOrderResponse, { dealId: string; data: CreateDealOrderData }>({
      query: ({ dealId, data }) => ({
        url: `/deals/${dealId}/order`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Deal", id: "LIST" }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetDealsQuery,
  useGetDealQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDeleteDealMutation,
  useCreateDealOrderMutation,
} = dealsApi;